import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { isShijiUser } from 'shared/interfaces/current-shiji-user.interface';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { ShijiPermissionsEnum } from 'shared/interfaces/permissions.interface';
import { Tab, TabKeyEnum } from 'shared/interfaces/tab.interface';
import { UserApiService } from 'shared/services';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { TabOutlineComponent } from '../../../shared/components/tab-outline/tab-outline.component';

@Component({
    selector: 'app-exports',
    templateUrl: './exports.component.html',
    styleUrl: './exports.component.scss',
    imports: [TranslateModule, RouterModule, TabOutlineComponent],
})
export class ExportsComponent implements OnInit {
    public tabs: Tab[] = [];
    isShiji: boolean = false;

    channelDistributionStatusTab: Tab = {
        label: 'exports.channelDistributionStatus.label',
        link: 'export-management/channel-distribution-status',
        key: TabKeyEnum.CHANNEL_DISTRIBUTION_STATUS,
    };

    exportsMonitoringTab: Tab = {
        label: 'exports.exports-monitoring.label',
        link: 'export-management/exports-monitoring',
        key: TabKeyEnum.EXPORTS_MONITORING,
    };

    importsMonitoringTab: Tab = {
        label: 'exports.imports-monitoring.label',
        link: 'export-management/imports-monitoring',
        key: TabKeyEnum.IMPORTS_MONITORING,
    };

    fullExportsTab: Tab = {
        label: 'exports.full-export.label',
        link: 'export-management/full-export',
        key: TabKeyEnum.FULL_EXPORT,
    };

    importsRunnerTab: Tab = {
        label: 'exports.imports-runner.label',
        link: 'export-management/imports-runner',
        key: TabKeyEnum.IMPORTS_RUNNER,
    };

    public selectedTabIndex = 0;

    constructor(
        private activatedRoute: ActivatedRoute,
        private userApiService: UserApiService,
        private router: Router,
        private destroyRef: DestroyRef,
        private featureFlagService: FeatureFlagService,
    ) {}

    ngOnInit(): void {
        this.buildTabs();
        this.setCurrentTab();
        this.subscribeToRouterEvents();
    }

    buildTabs() {
        const user = this.userApiService.currentUser;

        if (user && isShijiUser(user)) {
            this.isShiji = true;
            this.tabs.push(this.channelDistributionStatusTab);
            const hasBulkOperationsPermissions = user.shijiPermissions.some(
                (permission) => permission.isAuthorized && permission.shijiAccessZone === ShijiPermissionsEnum.BULK_OPERATIONS,
            );
            if (hasBulkOperationsPermissions) {
                this.tabs.push(this.importsRunnerTab);
                this.tabs.push(this.fullExportsTab);
            }
        } else {
            this.isShiji = false;
        }

        if (this.featureFlagService.featureFlags()[FeatureFlagEnum.MONITORING_TAB]) {
            this.tabs.push(this.exportsMonitoringTab);
        }

        if (user && isShijiUser(user)) {
            this.tabs.push(this.importsMonitoringTab);
        }
    }

    subscribeToRouterEvents() {
        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.setCurrentTab();
            });
    }

    setCurrentTab() {
        const currentTab = this.activatedRoute.snapshot.firstChild?.url[0].path;
        if (currentTab) {
            this.selectedTabIndex = this.tabs.findIndex((tab) => tab.key === currentTab);
        }
    }
}
